import type { CyclePhase, CycleSymptoms, Profile, WeightTrendPoint } from '@nutians/shared/schemas';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import {
  phaseForDate,
  todayLocalDate,
  useCreateCycleLog,
  useCycleConsent,
  useCycleLogs,
  useCyclePhase,
  useUpdateCycleConsent,
} from '../hooks/useCycle.js';
import { useCreateWeight, useWeightRange } from '../hooks/useWeight.js';

type Units = 'metric' | 'imperial';
type EventType = 'period_start' | 'period_end';
type CycleSegment = {
  phase: CyclePhase;
  startIndex: number;
  endIndex: number;
  lateLuteal: boolean;
};

const SYMPTOM_KEYS = ['bloating', 'cravings', 'energy', 'mood'] as const;

function formatDate(date: string | null | undefined, locale: string): string | null {
  if (!date) return null;
  return new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric' }).format(
    new Date(`${date}T12:00:00`),
  );
}

function kgToLbs(kg: number): number {
  return Math.round(kg * 2.20462 * 10) / 10;
}

function lbsToKg(lbs: number): number {
  return Math.round((lbs / 2.20462) * 10) / 10;
}

function formatSigned(value: number, unit: string): string {
  const rounded = Math.round(value * 10) / 10;
  if (Math.abs(rounded) < 0.1) return `~0 ${unit}/week`;
  return `~${rounded > 0 ? '+' : ''}${rounded.toFixed(1)} ${unit}/week`;
}

function displayWeight(kg: number, units: Units): number {
  return units === 'imperial' ? kgToLbs(kg) : Math.round(kg * 10) / 10;
}

function toKg(value: number, units: Units): number {
  return units === 'imperial' ? lbsToKg(value) : Math.round(value * 10) / 10;
}

function trendSlope(points: WeightTrendPoint[], units: Units): number | null {
  if (points.length < 2) return null;
  const first = points[0];
  const last = points.at(-1);
  if (!first || !last) return null;
  const days =
    (new Date(`${last.localDate}T12:00:00Z`).getTime() -
      new Date(`${first.localDate}T12:00:00Z`).getTime()) /
    86_400_000;
  if (days <= 0) return null;
  const kgPerWeek = ((last.trendKg - first.trendKg) / days) * 7;
  return units === 'imperial' ? kgToLbs(kgPerWeek) : kgPerWeek;
}

function WeightGraph({
  points,
  units,
  cycleSegments = [],
}: {
  points: WeightTrendPoint[];
  units: Units;
  cycleSegments?: CycleSegment[];
}) {
  const chartPoints = points.slice(-42);
  if (chartPoints.length < 2) {
    return (
      <div className="flex h-36 items-center justify-center rounded-xl bg-husk/50 text-sm text-ink-soft">
        Add a few weigh-ins to see the smoothed trend.
      </div>
    );
  }

  const values = chartPoints.flatMap((point) => [
    displayWeight(point.scaleKg, units),
    displayWeight(point.trendKg, units),
  ]);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const pad = Math.max((max - min) * 0.2, units === 'imperial' ? 1 : 0.5);
  const yMin = min - pad;
  const yMax = max + pad;
  const yFor = (value: number) => 132 - ((value - yMin) / (yMax - yMin || 1)) * 112;
  const xFor = (index: number) => 16 + (index / Math.max(1, chartPoints.length - 1)) * 288;
  const trendPath = chartPoints
    .map((point, index) => `${xFor(index)},${yFor(displayWeight(point.trendKg, units))}`)
    .join(' ');

  return (
    <svg
      viewBox="0 0 320 144"
      role="img"
      aria-label="Smoothed weight trend chart"
      className="h-36 w-full"
    >
      <line x1="16" x2="304" y1="132" y2="132" stroke="var(--color-husk)" strokeWidth="2" />
      {cycleSegments.map((segment) => {
        const x = xFor(segment.startIndex);
        const width = Math.max(8, xFor(segment.endIndex) - x + 6);
        const fill = segment.lateLuteal ? 'var(--color-acorn)' : 'var(--color-mist)';
        return (
          <rect
            key={`${segment.phase}-${segment.startIndex}-${segment.endIndex}-${segment.lateLuteal}`}
            x={x - 3}
            y="12"
            width={width}
            height="120"
            rx="6"
            fill={fill}
            opacity={segment.lateLuteal ? '0.13' : '0.08'}
          />
        );
      })}
      {chartPoints.map((point, index) => (
        <circle
          key={`${point.id}-scale`}
          cx={xFor(index)}
          cy={yFor(displayWeight(point.scaleKg, units))}
          r="3"
          fill="var(--color-mist)"
          opacity="0.28"
        />
      ))}
      <polyline
        fill="none"
        points={trendPath}
        stroke="var(--color-leaf)"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="4"
      />
      {chartPoints.map((point, index) => (
        <circle
          key={`${point.id}-trend`}
          cx={xFor(index)}
          cy={yFor(displayWeight(point.trendKg, units))}
          r={index === chartPoints.length - 1 ? 4 : 2}
          fill="var(--color-leaf)"
        />
      ))}
    </svg>
  );
}

function CyclePanel({
  hasConsent,
  compact,
  chartPoints,
  cycleLength,
  setCycleLength,
}: {
  hasConsent: boolean;
  compact: boolean;
  chartPoints: WeightTrendPoint[];
  cycleLength: number;
  setCycleLength: (value: number) => void;
}) {
  const { t, i18n } = useTranslation(['cycle', 'common']);
  const [eventType, setEventType] = useState<EventType>('period_start');
  const [occurredOn, setOccurredOn] = useState(todayLocalDate());
  const [symptoms, setSymptoms] = useState<CycleSymptoms>({});
  // ponytail: ephemeral checkbox — gates opt-in only; nothing to persist since
  // consent stays false and consent already drives all phase logic.
  const [usesHormonalBc, setUsesHormonalBc] = useState(false);
  const consentMutation = useUpdateCycleConsent();
  const logs = useCycleLogs(hasConsent);
  const phase = useCyclePhase(hasConsent, cycleLength);
  const createLog = useCreateCycleLog();

  const latestLogs = logs.data?.logs.slice(0, compact ? 2 : 4) ?? [];
  const nextPeriod = formatDate(phase.data?.nextPeriodStart, i18n.language);
  const phaseLabel = phase.data ? t(`cycle:phase.${phase.data.phase}`) : null;

  function toggleSymptom(key: (typeof SYMPTOM_KEYS)[number]) {
    setSymptoms((current) => ({ ...current, [key]: !current[key] }));
  }

  function submitCycleLog(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const selected = Object.fromEntries(
      Object.entries(symptoms).filter(([, selected]) => selected),
    ) as CycleSymptoms;
    createLog.mutate(
      {
        eventType,
        occurredOn,
        symptoms: Object.keys(selected).length > 0 ? selected : null,
      },
      {
        onSuccess: () => setSymptoms({}),
      },
    );
  }

  if (!hasConsent) {
    return (
      <div className="mt-4 rounded-xl border border-husk bg-husk/35 p-4 text-sm leading-6 text-ink-soft">
        <p className="font-medium text-ink">{t('cycle:onboarding.title')}</p>
        <p className="mt-1">{t('cycle:onboarding.body')}</p>
        <label className="mt-3 flex items-start gap-2 text-sm text-ink-soft">
          <input
            type="checkbox"
            checked={usesHormonalBc}
            onChange={(event) => setUsesHormonalBc(event.currentTarget.checked)}
            className="mt-0.5 h-3.5 w-3.5 rounded border-ink/20 text-leaf focus:ring-leaf"
          />
          {t('cycle:onboarding.pillLabel')}
        </label>
        {usesHormonalBc ? (
          <p className="mt-2 rounded-lg bg-leaf/5 p-3 text-sm leading-6 text-ink-soft">
            {t('cycle:onboarding.pillNote')}
          </p>
        ) : (
          <button
            type="button"
            onClick={() => consentMutation.mutate(true)}
            disabled={consentMutation.isPending}
            className="mt-3 rounded-xl bg-leaf px-4 py-2 text-sm font-semibold text-white hover:bg-leaf-deep disabled:opacity-50"
          >
            {t('cycle:onboarding.enable')}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="mt-4 rounded-xl border border-husk bg-husk/30 p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="text-sm font-medium text-ink">{t('cycle:panel.title')}</p>
          <p className="mt-1 text-sm leading-6 text-ink-soft">
            {nextPeriod
              ? t('cycle:panel.nextPeriod', { date: nextPeriod })
              : t('cycle:panel.noPrediction')}
          </p>
          {phaseLabel ? (
            <p className="text-xs leading-5 text-ink-soft">
              {t('cycle:panel.phaseNow', { phase: phaseLabel })}
              {phase.data?.lateLutealWindow ? ` ${t('cycle:panel.waterWindow')}` : ''}
            </p>
          ) : null}
        </div>
        <label className="text-xs font-medium text-ink-soft">
          {t('cycle:panel.lengthLabel')}
          <input
            type="number"
            min={21}
            max={45}
            value={cycleLength}
            onChange={(event) => {
              const parsed = Number(event.target.value);
              if (Number.isFinite(parsed)) setCycleLength(Math.min(45, Math.max(21, parsed)));
            }}
            className="mt-1 block w-24 rounded-lg border border-husk bg-shell px-3 py-2 text-sm text-ink outline-none focus:border-leaf focus:ring-2 focus:ring-leaf/25"
          />
        </label>
      </div>

      <form onSubmit={submitCycleLog} className="mt-4 space-y-3">
        <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_9rem]">
          <div className="grid grid-cols-2 gap-2">
            {(['period_start', 'period_end'] as const).map((type) => (
              <button
                key={type}
                type="button"
                onClick={() => setEventType(type)}
                className={`rounded-xl border px-3 py-2 text-sm font-medium ${
                  eventType === type
                    ? 'border-leaf bg-leaf/10 text-leaf'
                    : 'border-husk bg-shell text-ink-soft'
                }`}
              >
                {t(`cycle:event.${type}`)}
              </button>
            ))}
          </div>
          <input
            type="date"
            value={occurredOn}
            onChange={(event) => setOccurredOn(event.currentTarget.value)}
            className="rounded-xl border border-husk bg-shell px-3 py-2 text-sm text-ink outline-none focus:border-leaf focus:ring-2 focus:ring-leaf/25"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          {SYMPTOM_KEYS.map((key) => (
            <label
              key={key}
              className="flex items-center gap-2 rounded-full border border-husk bg-shell px-3 py-1.5 text-xs text-ink-soft"
            >
              <input
                type="checkbox"
                checked={symptoms[key] === true}
                onChange={() => toggleSymptom(key)}
                className="h-3.5 w-3.5 rounded border-ink/20 text-leaf focus:ring-leaf"
              />
              {t(`cycle:symptom.${key}`)}
            </label>
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="submit"
            disabled={createLog.isPending}
            className="rounded-xl bg-leaf px-4 py-2 text-sm font-semibold text-white hover:bg-leaf-deep disabled:opacity-50"
          >
            {createLog.isPending ? t('cycle:panel.saving') : t('cycle:panel.log')}
          </button>
          <button
            type="button"
            onClick={() => consentMutation.mutate(false)}
            disabled={consentMutation.isPending}
            className="rounded-xl border border-husk px-4 py-2 text-sm font-medium text-ink-soft hover:bg-husk/40 disabled:opacity-50"
          >
            {t('cycle:panel.disable')}
          </button>
        </div>
      </form>

      {!compact && latestLogs.length > 0 ? (
        <div className="mt-4 border-t border-husk pt-3 text-xs leading-5 text-ink-soft">
          <p className="font-medium text-ink">{t('cycle:panel.recent')}</p>
          <ul className="mt-1 space-y-1">
            {latestLogs.map((log) => (
              <li key={log.id}>
                {formatDate(log.occurredOn, i18n.language)} · {t(`cycle:event.${log.eventType}`)}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {chartPoints.length >= 2 ? (
        <p className="mt-3 text-xs leading-5 text-ink-soft">{t('cycle:panel.overlayHelp')}</p>
      ) : null}
    </div>
  );
}

export function WeightTrendCard({
  profile,
  compact = false,
  showViewLink = false,
}: {
  profile?: Profile | null;
  compact?: boolean;
  showViewLink?: boolean;
}) {
  const units = profile?.unitsSystem ?? 'metric';
  const { t } = useTranslation('cycle');
  const unitLabel = units === 'imperial' ? 'lb' : 'kg';
  const flaggedEd = profile?.flaggedEd === true;
  const { data, isLoading } = useWeightRange(compact ? 42 : 84);
  const mutation = useCreateWeight();
  const [value, setValue] = useState('');
  const cycleConsent = useCycleConsent();

  const points = data?.points ?? [];
  const chartPoints = points.slice(-42);
  const latest = points.at(-1);
  const slope = useMemo(() => trendSlope(points.slice(-28), units), [points, units]);
  const hasCycleConsent =
    cycleConsent.data?.consents.find((item) => item.purpose === 'cycle')?.granted ?? false;
  const [cycleLength, setCycleLength] = useState(() => {
    const stored = window.localStorage.getItem('nutians_cycle_length_days');
    const parsed = Number(stored);
    return Number.isFinite(parsed) && parsed >= 21 && parsed <= 45 ? parsed : 28;
  });
  const cycleLogs = useCycleLogs(hasCycleConsent);
  useEffect(() => {
    window.localStorage.setItem('nutians_cycle_length_days', String(cycleLength));
  }, [cycleLength]);
  const cycleSegments = useMemo(() => {
    if (!hasCycleConsent || chartPoints.length < 2) return [];
    const segments: CycleSegment[] = [];
    for (const point of chartPoints) {
      const phase = phaseForDate(cycleLogs.data, point.localDate, cycleLength);
      const phaseName =
        phase.phase === 'unknown' || phase.phase === 'follicular' ? null : phase.phase;
      if (!phaseName) continue;
      const index = chartPoints.indexOf(point);
      const previous = segments.at(-1);
      if (
        previous &&
        previous.phase === phaseName &&
        previous.lateLuteal === phase.lateLutealWindow &&
        previous.endIndex === index - 1
      ) {
        previous.endIndex = index;
      } else {
        segments.push({
          phase: phaseName,
          startIndex: index,
          endIndex: index,
          lateLuteal: phase.lateLutealWindow,
        });
      }
    }
    return segments;
  }, [chartPoints, cycleLength, cycleLogs.data, hasCycleConsent]);

  function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const parsed = Number(value.replace(',', '.'));
    if (!Number.isFinite(parsed) || parsed <= 0) return;
    mutation.mutate(
      { weightKg: toKg(parsed, units) },
      {
        onSuccess: () => setValue(''),
      },
    );
  }

  return (
    <section className="rounded-2xl border border-husk bg-white p-5 dark:border-ink/20 dark:bg-shell">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex items-center gap-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-ink-soft">
              Weight trend
            </p>
            {showViewLink ? (
              <Link to="/weight" className="text-xs font-medium text-leaf hover:text-leaf-deep">
                View
              </Link>
            ) : null}
          </div>
          {latest ? (
            <p className="mt-1 text-2xl font-semibold tabular-nums text-ink">
              {displayWeight(latest.trendKg, units).toFixed(1)} {unitLabel}
            </p>
          ) : (
            <p className="mt-1 text-base font-medium text-ink">Start with one weigh-in</p>
          )}
        </div>
        <form onSubmit={submit} className="flex min-w-0 gap-2">
          <label className="sr-only" htmlFor="weight-input">
            Weight in {unitLabel}
          </label>
          <input
            id="weight-input"
            inputMode="decimal"
            value={value}
            onChange={(event) => setValue(event.target.value)}
            placeholder={unitLabel}
            className="w-24 rounded-xl border border-husk bg-shell px-3 py-2 text-sm text-ink outline-none focus:border-leaf focus:ring-2 focus:ring-leaf/25"
          />
          <button
            type="submit"
            disabled={mutation.isPending || value.trim() === ''}
            className="rounded-xl bg-leaf px-4 py-2 text-sm font-semibold text-white hover:bg-leaf-deep disabled:opacity-50"
          >
            Log
          </button>
        </form>
      </div>

      {isLoading ? (
        <div className="h-36 rounded-xl bg-husk/50" />
      ) : points.length === 0 ? (
        <>
          <div className="rounded-xl bg-husk/50 px-4 py-6 text-sm leading-6 text-ink-soft">
            Add a first weigh-in whenever you are ready. The smoothed line will appear as you add
            more entries.
          </div>
          <CyclePanel
            hasConsent={hasCycleConsent}
            compact={compact}
            chartPoints={chartPoints}
            cycleLength={cycleLength}
            setCycleLength={setCycleLength}
          />
        </>
      ) : (
        <>
          <WeightGraph points={points} units={units} cycleSegments={cycleSegments} />
          <div className="mt-3 space-y-1 text-sm leading-6 text-ink-soft">
            <p>Your weight bounces day to day; the line is the real trend.</p>
            {flaggedEd ? (
              <p>Use the smoothed line as context.</p>
            ) : slope !== null ? (
              <p>Trend changed by {formatSigned(slope, unitLabel)}.</p>
            ) : null}
            {hasCycleConsent && cycleSegments.length > 0 ? <p>{t('chart.context')}</p> : null}
          </div>
          <CyclePanel
            hasConsent={hasCycleConsent}
            compact={compact}
            chartPoints={chartPoints}
            cycleLength={cycleLength}
            setCycleLength={setCycleLength}
          />
        </>
      )}
    </section>
  );
}
